import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  AdminButton,
  AdminCard,
  AdminCardHeader,
  AdminErrorBanner,
  AdminFilterChips,
  AdminMetricCard,
  AdminPageLayout,
  AdminSearchInput,
  AdminStatusBadge,
  AdminTable,
  AdminTd,
  AdminTr,
} from "@/components/admin";
import { fetchAdminFamilyConnectionDetail, fetchAdminFamilyConnections, fetchAdminSeniorFamilyConnectionSummaries } from "@/lib/api";
import { formatDateTime } from "@/lib/format";
import type { AdminFamilyConnectionDetail, AdminFamilyConnectionItem, AdminSeniorFamilyConnectionSummary } from "@/types";

type ConnectionFilter = "all" | "connected" | "unconnected";

const accountStatusLabels: Record<string, string> = {
  ACTIVE: "활성",
  INACTIVE: "비활성",
  DELETED: "삭제",
};

const FamilyConnectionsPage = () => {
  const navigate = useNavigate();
  const [seniors, setSeniors] = useState<AdminSeniorFamilyConnectionSummary[]>([]);
  const [connections, setConnections] = useState<AdminFamilyConnectionItem[]>([]);
  const [selectedConnection, setSelectedConnection] = useState<AdminFamilyConnectionDetail | null>(null);
  const [keyword, setKeyword] = useState("");
  const [connectionFilter, setConnectionFilter] = useState<ConnectionFilter>("all");
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const loadSeniors = useCallback(async () => {
    setLoading(true);
    setErrorMessage(null);
    try {
      const [nextSeniors, nextConnections] = await Promise.all([
        fetchAdminSeniorFamilyConnectionSummaries(),
        fetchAdminFamilyConnections(),
      ]);
      setSeniors(nextSeniors);
      setConnections(nextConnections);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "Senior 연결 현황을 불러오지 못했습니다.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadSeniors();
  }, [loadSeniors]);

  const connectedSeniorCount = useMemo(
    () => seniors.filter((senior) => senior.activeConnectionCount > 0).length,
    [seniors],
  );
  const unconnectedSeniorCount = seniors.length - connectedSeniorCount;

  const filterChips = useMemo(
    () => [
      { key: "all", label: "전체", count: seniors.length },
      { key: "connected", label: "연결 있음", count: connectedSeniorCount },
      { key: "unconnected", label: "미연결", count: unconnectedSeniorCount },
    ],
    [connectedSeniorCount, seniors.length, unconnectedSeniorCount],
  );

  const filteredSeniors = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    return seniors.filter((senior) => {
      const matchesConnection =
        connectionFilter === "all"
        || (connectionFilter === "connected" && senior.activeConnectionCount > 0)
        || (connectionFilter === "unconnected" && senior.activeConnectionCount === 0);
      if (!matchesConnection) return false;
      if (!normalizedKeyword) return true;
      return [
        senior.seniorUserAccountId,
        senior.seniorLoginId,
        senior.seniorName,
      ].some((value) => String(value).toLowerCase().includes(normalizedKeyword));
    });
  }, [connectionFilter, keyword, seniors]);

  const filteredConnections = useMemo(() => {
    const normalizedKeyword = keyword.trim().toLowerCase();
    return connections.filter((connection) => {
      if (!normalizedKeyword) return true;
      return [
        connection.connectionId,
        connection.seniorUserAccountId,
        connection.seniorLoginId,
        connection.seniorName,
        connection.keeperUserAccountId,
        connection.keeperLoginId,
        connection.keeperName,
        connection.relationshipLabel,
      ].some((value) => String(value ?? "").toLowerCase().includes(normalizedKeyword));
    });
  }, [connections, keyword]);

  const activeConnectionCount = connections.filter((connection) => connection.connectionStatus === "ACTIVE").length;
  const disconnectedConnectionCount = connections.length - activeConnectionCount;

  const openConnectionDetail = async (connectionId: number) => {
    setErrorMessage(null);
    try {
      setSelectedConnection(await fetchAdminFamilyConnectionDetail(connectionId));
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : "직접 연결 상세를 불러오지 못했습니다.");
    }
  };

  const openDetail = (seniorUserAccountId: number) => {
    navigate(`/admin/family-connections/${seniorUserAccountId}`);
  };

  return (
    <AdminPageLayout
      title="가족 연결 관리"
      subtitle="Senior별 연결 계정 수를 확인하고 상세에서 Keeper 연결을 관리합니다."
      actions={
        <AdminButton variant="soft" size="sm" icon="ri-refresh-line" onClick={() => void loadSeniors()}>
          새로고침
        </AdminButton>
      }
    >
      <AdminErrorBanner message={errorMessage} />

      <section className="mb-4 mt-4 grid grid-cols-1 gap-3 md:grid-cols-3">
        <AdminMetricCard title="전체 Senior" value={`${seniors.length}명`} icon="ri-user-heart-line" tone="blue" />
        <AdminMetricCard title="연결된 Senior" value={`${connectedSeniorCount}명`} icon="ri-link" tone="teal" />
        <AdminMetricCard title="미연결 Senior" value={`${unconnectedSeniorCount}명`} icon="ri-link-unlink" tone="slate" />
      </section>

      <div className="mb-4 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="mb-1 text-xs font-semibold text-slate-500">Senior 연결 현황 필터</p>
            <AdminFilterChips
              variant="inline"
              chips={filterChips}
              activeKey={connectionFilter}
              onChange={(key) => setConnectionFilter(key as ConnectionFilter)}
            />
          </div>
          <AdminSearchInput
            value={keyword}
            onChange={setKeyword}
            placeholder="Senior 이름, 로그인 ID, 계정 ID 검색"
            className="w-full lg:w-96"
          />
        </div>
      </div>

      <AdminCard className="overflow-hidden">
        <AdminCardHeader
          icon="ri-user-heart-line"
          title={`Senior 연결 현황 · ${filteredSeniors.length}명 표시`}
        />
        <AdminTable
          headers={["Senior", "연결 계정 수", "계정 상태", "관리"]}
          loading={loading}
          emptyMessage={!loading && filteredSeniors.length === 0 ? "조건에 맞는 Senior가 없습니다." : undefined}
        >
          {filteredSeniors.map((senior) => (
            <AdminTr
              key={senior.seniorUserAccountId}
              className="cursor-pointer hover:bg-slate-50"
              onClick={() => openDetail(senior.seniorUserAccountId)}
            >
              <AdminTd>
                <div className="font-semibold text-slate-700">{senior.seniorName}</div>
                <div className="mt-0.5 text-xs text-slate-400">
                  #{senior.seniorUserAccountId} · {senior.seniorLoginId}
                </div>
              </AdminTd>
              <AdminTd>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold text-slate-800">{senior.activeConnectionCount}명</span>
                  <AdminStatusBadge
                    label={senior.activeConnectionCount > 0 ? "연결 있음" : "미연결"}
                    tone={senior.activeConnectionCount > 0 ? "success" : "slate"}
                  />
                </div>
              </AdminTd>
              <AdminTd>
                <AdminStatusBadge
                  label={accountStatusLabels[senior.accountStatus] ?? senior.accountStatus}
                  tone={senior.accountStatus === "ACTIVE" ? "success" : "slate"}
                />
              </AdminTd>
              <AdminTd>
                <AdminButton variant="secondary" size="sm" onClick={() => openDetail(senior.seniorUserAccountId)}>
                  상세 보기
                </AdminButton>
              </AdminTd>
            </AdminTr>
          ))}
        </AdminTable>
      </AdminCard>

      <AdminCard className="mt-4 overflow-hidden">
        <AdminCardHeader
          icon="ri-links-line"
          title={`Senior·Keeper 직접 연결 전체 이력 · ${filteredConnections.length}건 (활성 ${activeConnectionCount} · 해제 ${disconnectedConnectionCount})`}
        />
        <AdminTable
          headers={["Senior", "Keeper", "관계", "상태", "연결·해제 시각", "관리"]}
          loading={loading}
          emptyMessage={!loading && filteredConnections.length === 0 ? "조건에 맞는 직접 연결이 없습니다." : undefined}
        >
          {filteredConnections.map((connection) => (
            <AdminTr key={connection.connectionId}>
              <AdminTd>
                <div className="font-semibold text-slate-700">{connection.seniorName}</div>
                <div className="mt-0.5 text-xs text-slate-400">#{connection.seniorUserAccountId} · {connection.seniorLoginId}</div>
              </AdminTd>
              <AdminTd>
                <div className="font-semibold text-slate-700">{connection.keeperName}</div>
                <div className="mt-0.5 text-xs text-slate-400">#{connection.keeperUserAccountId} · {connection.keeperLoginId}</div>
              </AdminTd>
              <AdminTd>{connection.relationshipLabel ?? "-"}</AdminTd>
              <AdminTd><AdminStatusBadge label={connection.connectionStatus === "ACTIVE" ? "연결 중" : "연결 해제"} tone={connection.connectionStatus === "ACTIVE" ? "success" : "slate"} /></AdminTd>
              <AdminTd>
                <div className="text-xs text-slate-600">연결 {formatDateTime(connection.connectedAt)}</div>
                <div className="mt-1 text-xs text-slate-400">해제 {formatDateTime(connection.disconnectedAt)}</div>
              </AdminTd>
              <AdminTd><AdminButton variant="secondary" size="sm" onClick={() => void openConnectionDetail(connection.connectionId)}>연결 상세</AdminButton></AdminTd>
            </AdminTr>
          ))}
        </AdminTable>
      </AdminCard>

      {selectedConnection && (
        <AdminCard className="mt-4 overflow-hidden">
          <AdminCardHeader icon="ri-file-list-3-line" title={`직접 연결 #${selectedConnection.connectionId} 상세`} />
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4">
            <div className="border-b border-slate-100 p-4 md:border-r"><p className="text-xs text-slate-400">Senior</p><p className="mt-1 text-sm font-semibold text-slate-700">{selectedConnection.senior.displayName}</p><p className="text-xs text-slate-400">#{selectedConnection.senior.userAccountId} · {selectedConnection.senior.loginId}</p></div>
            <div className="border-b border-slate-100 p-4 xl:border-r"><p className="text-xs text-slate-400">Keeper</p><p className="mt-1 text-sm font-semibold text-slate-700">{selectedConnection.keeper.displayName}</p><p className="text-xs text-slate-400">#{selectedConnection.keeper.userAccountId} · {selectedConnection.keeper.loginId}</p></div>
            <div className="border-b border-slate-100 p-4 md:border-r"><p className="text-xs text-slate-400">연결 상태·관계</p><p className="mt-1 text-sm font-semibold text-slate-700">{selectedConnection.connectionStatus === "ACTIVE" ? "연결 중" : "연결 해제"} · {selectedConnection.relationshipLabel ?? "-"}</p><p className="text-xs text-slate-400">연결 {formatDateTime(selectedConnection.connectedAt)} · 해제 {formatDateTime(selectedConnection.disconnectedAt)}</p></div>
            <div className="border-b border-slate-100 p-4"><p className="text-xs text-slate-400">원본 저장 시각</p><p className="mt-1 text-xs text-slate-600">생성 {formatDateTime(selectedConnection.createdAt)}</p><p className="text-xs text-slate-400">수정 {formatDateTime(selectedConnection.updatedAt)}</p></div>
          </div>
        </AdminCard>
      )}
    </AdminPageLayout>
  );
};

export default FamilyConnectionsPage;
